/**
 * Display formatting utilities for numbers, prices, quantities, etc.
 * Provides consistent formatting across all components.
 */

import { fromSatoshi } from './math';

/**
 * Format a number with specified decimal places
 * @param value - The number to format
 * @param minDecimals - Minimum decimal places (default: 2)
 * @param maxDecimals - Maximum decimal places (default: 2)
 * @param fallback - Value to return if value is invalid (default: "0.00")
 * @returns Formatted string
 */
export function formatNumber(
  value: number | undefined | null,
  minDecimals: number = 2,
  maxDecimals: number = 2,
  fallback: string = "0.00",
): string {
  if (value === undefined || value === null || isNaN(value)) return fallback;
  return value.toLocaleString("en-US", {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Format a price value (2 decimal places, shows "--" for invalid)
 * @param price - The price to format
 * @returns Formatted price string
 */
export function formatPrice(price: number | undefined | null): string {
  if (price === undefined || price === null || isNaN(price)) return "--";
  return formatNumber(price, 2, 2, "--");
}

/**
 * Format a price with flexible decimal places (2-8 decimals)
 * @param price - The price to format
 * @returns Formatted price string
 */
export function formatPriceFlexible(price: number | undefined | null): string {
  if (price === undefined || price === null || isNaN(price)) return "--";
  return formatNumber(price, 2, 8, "--");
}

/**
 * Format a size/quantity value (3 decimal places)
 * @param size - The size to format
 * @returns Formatted size string
 */
export function formatSize(size: number | undefined | null): string {
  if (size === undefined || size === null || isNaN(size)) return "0.000";
  return formatNumber(size, 3, 3, "0.000");
}

/**
 * Format a quantity value (3-8 decimal places)
 * @param quantity - The quantity to format
 * @returns Formatted quantity string
 */
export function formatQuantity(quantity: number | undefined | null): string {
  if (quantity === undefined || quantity === null || isNaN(quantity))
    return "0";
  return formatNumber(quantity, 3, 8, "0");
}

/**
 * Format a margin value (2 decimal places)
 * @param margin - The margin to format
 * @returns Formatted margin string
 */
export function formatMargin(margin: number | undefined | null): string {
  if (margin === undefined || margin === null || isNaN(margin)) return "0.00";
  return formatNumber(margin, 2, 2, "0.00");
}

/**
 * Format a P/L (Profit/Loss) value (2 decimal places)
 * @param pnl - The P/L to format
 * @returns Formatted P/L string
 */
export function formatPnL(pnl: number | undefined | null): string {
  if (pnl === undefined || pnl === null || isNaN(pnl)) return "0.00";
  return formatNumber(pnl, 2, 2, "0.00");
}

/**
 * Format a currency value (2 decimal places)
 * Alias for formatMargin, but semantically clearer for currency values
 * @param value - The currency value to format
 * @returns Formatted currency string
 */
export function formatCurrency(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return "0.00";
  return formatNumber(value, 2, 2, "0.00");
}

/**
 * Format a percentage value (multiplies by 100, shows 2 decimal places)
 * @param value - The ratio value (0.75 for 75%)
 * @returns Formatted percentage string (e.g., "75.00%")
 */
export function formatPercentage(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return "0%";
  return `${(value * 100).toFixed(2)}%`;
}

/**
 * Format a Satoshi-like number (convert from satoshi scale, format with 8 decimals, trim trailing zeros)
 * Used for blockchain-based values that are stored in smallest units
 * Uses fromSatoshi from math.ts for consistent conversion with BigInt precision
 * @param value - The Satoshi value to format (number or string)
 * @returns Formatted decimal string
 */
export function formatSatoshiNumber(
  value: number | string | undefined | null,
): string {
  if (value === undefined || value === null) return "0";
  
  // Handle string values that might be invalid
  if (typeof value === 'string' && (value.trim() === '' || isNaN(Number(value)))) {
    return "0";
  }
  
  try {
    // Use fromSatoshi for consistent conversion (handles BigInt precision)
    const decimal = fromSatoshi(value);
    // Format with 8 decimals, then trim trailing zeros and decimal point if needed
    return decimal.toFixed(8).replace(/\.?0+$/, "");
  } catch (error) {
    // Fallback for invalid values
    return "0";
  }
}

/**
 * Format a price from Satoshi format (convert from satoshi scale, add thousands separators)
 * Uses fromSatoshi from math.ts for consistent conversion
 * @param price - The Satoshi price value (number or string)
 * @returns Formatted price string with thousands separators
 */
export function formatPriceFromSatoshi(
  price: number | string | undefined | null,
): string {
  if (price === undefined || price === null) return "--";
  
  // Handle string values that might be invalid
  if (typeof price === 'string' && (price.trim() === '' || isNaN(Number(price)))) {
    return "--";
  }
  
  try {
    const formatted = formatSatoshiNumber(price);
    // Add thousands separators for readability
    const parts = formatted.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  } catch (error) {
    return "--";
  }
}

/**
 * Format a quantity from Satoshi format (convert from satoshi scale)
 * Uses fromSatoshi from math.ts for consistent conversion
 * @param quantity - The Satoshi quantity value (number or string)
 * @returns Formatted quantity string
 */
export function formatQuantityFromSatoshi(
  quantity: number | string | undefined | null,
): string {
  if (quantity === undefined || quantity === null) return "0";
  
  // Handle string values that might be invalid
  if (typeof quantity === 'string' && (quantity.trim() === '' || isNaN(Number(quantity)))) {
    return "0";
  }
  
  try {
    return formatSatoshiNumber(quantity);
  } catch (error) {
    return "0";
  }
}

